(() => {
  const SECTION_SELECTOR = '.insieme-v2[data-section-id]';
  const FORMAT_SELECTOR = '[data-insieme-format-card]';
  const CHUTNEY_SELECTOR = '[data-insieme-chutney-card]';
  const ACCOMPANIMENT_SELECTOR = '[data-insieme-accompaniments]';

  const MIN_PRODUCT_QUANTITY = 1;
  const MAX_PRODUCT_QUANTITY = 99;
  const MAX_CHUTNEY_QUANTITY = 99;

  function clamp(value, minimum, maximum) {
    return Math.min(maximum, Math.max(minimum, value));
  }

  function getNumber(value, fallback = 0) {
    const number = Number.parseInt(value, 10);
    return Number.isNaN(number) ? fallback : number;
  }

  function getProductQuantity(section) {
    return clamp(
      getNumber(section.dataset.quantity, 1),
      MIN_PRODUCT_QUANTITY,
      MAX_PRODUCT_QUANTITY
    );
  }

  function getSelectedFormat(section) {
    return (
      section.querySelector(`${FORMAT_SELECTOR}.is-selected`) ||
      section.querySelector(FORMAT_SELECTOR)
    );
  }

  function getPeople(section) {
    const selectedFormat = getSelectedFormat(section);
    const peoplePerProduct = getNumber(selectedFormat?.dataset.people, 0);
    return peoplePerProduct * getProductQuantity(section);
  }

  function getGlutenFreePortions(section) {
    const root = section.querySelector(ACCOMPANIMENT_SELECTOR);
    return clamp(
      getNumber(root?.dataset.glutenFreePortions, 0),
      0,
      getPeople(section)
    );
  }

  function formatMoney(section, cents) {
    const locale = section.dataset.moneyLocale || 'it-IT';
    const currency = section.dataset.moneyCurrency || 'EUR';

    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(cents / 100);
    } catch (error) {
      return `${(cents / 100).toFixed(2).replace('.', ',')} €`;
    }
  }

  function getRequiredChutneys(section) {
    const selectedFormat = getSelectedFormat(section);

    if (!selectedFormat) return 0;

    const includedPerProduct = getNumber(
      selectedFormat.dataset.includedChutneys,
      0
    );

    return includedPerProduct * getProductQuantity(section);
  }

  function getSelectedChutneys(section) {
    let total = 0;

    section.querySelectorAll(CHUTNEY_SELECTOR).forEach((card) => {
      total += getNumber(card.dataset.chutneyQuantity, 0);
    });

    return total;
  }

  function getExtraChutneys(section) {
    return Math.max(
      0,
      getSelectedChutneys(section) - getRequiredChutneys(section)
    );
  }

  function updateProductQuantityInterface(section) {
    const quantity = getProductQuantity(section);

    section.dataset.quantity = String(quantity);

    const value = section.querySelector(
      '[data-insieme-quantity-value]'
    );

    const input = section.querySelector(
      '[data-insieme-quantity-input]'
    );

    const decrease = section.querySelector(
      '[data-insieme-quantity-decrease]'
    );

    const increase = section.querySelector(
      '[data-insieme-quantity-increase]'
    );

    if (value) {
      value.textContent = String(quantity);
    }

    if (input) {
      input.value = String(quantity);
    }

    if (decrease) {
      decrease.disabled = quantity <= MIN_PRODUCT_QUANTITY;
    }

    if (increase) {
      increase.disabled = quantity >= MAX_PRODUCT_QUANTITY;
    }
  }

  function updateChutneyCards(section) {
    section.querySelectorAll(CHUTNEY_SELECTOR).forEach((card) => {
      const quantity = clamp(
        getNumber(card.dataset.chutneyQuantity, 0),
        0,
        MAX_CHUTNEY_QUANTITY
      );

      card.dataset.chutneyQuantity = String(quantity);
      card.classList.toggle('is-selected', quantity > 0);

      const value = card.querySelector(
        '[data-insieme-chutney-value]'
      );

      const decrease = card.querySelector(
        '[data-insieme-chutney-decrease]'
      );

      const increase = card.querySelector(
        '[data-insieme-chutney-increase]'
      );

      if (value) {
        value.textContent = String(quantity);
      }

      if (decrease) {
        decrease.disabled = quantity <= 0;
      }

      if (increase) {
        increase.disabled = quantity >= MAX_CHUTNEY_QUANTITY;
      }
    });
  }

  function updateChutneyProgress(section) {
    const required = getRequiredChutneys(section);
    const selected = getSelectedChutneys(section);
    const extra = Math.max(0, selected - required);

    const badge = section.querySelector(
      '[data-insieme-included-badge]'
    );

    const progressText = section.querySelector(
      '[data-insieme-chutney-progress]'
    );

    const progressBar = section.querySelector(
      '[data-insieme-chutney-progress-bar]'
    );

    if (badge) {
      badge.textContent =
        required === 1
          ? '1 incluso'
          : `${required} inclusi`;

      badge.dataset.requiredChutneys = String(required);
    }

    if (progressText) {
      if (selected < required) {
        progressText.textContent =
          `Selezionati ${selected} di ${required}`;
      } else if (selected === required) {
        progressText.textContent =
          `${selected} selezionati`;
      } else {
        progressText.textContent =
          `${selected} selezionati · ${extra} extra`;
      }
    }

    if (progressBar) {
      const percentage =
        required > 0
          ? Math.min(100, (selected / required) * 100)
          : 0;

      progressBar.style.width = `${percentage}%`;
    }

    section.dataset.requiredChutneys = String(required);
    section.dataset.selectedChutneys = String(selected);
    section.dataset.extraChutneys = String(extra);
  }

  function updateAccompaniments(section) {
    const root = section.querySelector(ACCOMPANIMENT_SELECTOR);
    if (!root) return;

    const people = getPeople(section);
    const glutenFree = clamp(
      getNumber(root.dataset.glutenFreePortions, 0),
      0,
      people
    );
    const standard = Math.max(0, people - glutenFree);

    root.dataset.people = String(people);
    root.dataset.standardPortions = String(standard);
    root.dataset.glutenFreePortions = String(glutenFree);
    section.dataset.people = String(people);
    section.dataset.standardPortions = String(standard);
    section.dataset.glutenFreePortions = String(glutenFree);

    root.querySelectorAll('[data-insieme-accompaniment-people]').forEach((item) => {
      item.textContent = String(people);
    });

    const standardOutput = root.querySelector('[data-insieme-standard-portions]');
    const glutenFreeOutput = root.querySelector('[data-insieme-gluten-free-portions]');
    const decrease = root.querySelector('[data-insieme-gluten-free-decrease]');
    const increase = root.querySelector('[data-insieme-gluten-free-increase]');

    if (standardOutput) standardOutput.textContent = String(standard);
    if (glutenFreeOutput) glutenFreeOutput.textContent = String(glutenFree);
    if (decrease) decrease.disabled = glutenFree <= 0;
    if (increase) increase.disabled = glutenFree >= people;

    root.querySelector('[data-insieme-accompaniment-standard]')
      ?.classList.toggle('is-selected', standard > 0);
    root.querySelector('[data-insieme-accompaniment-gluten-free]')
      ?.classList.toggle('is-selected', glutenFree > 0);
  }

  function updatePrices(section) {
    const selectedFormat = getSelectedFormat(section);

    if (!selectedFormat) return;

    const quantity = getProductQuantity(section);
    const unitPrice = getNumber(selectedFormat.dataset.price, 0);
    const basePrice = unitPrice * quantity;

    const extraQuantity = getExtraChutneys(section);
    const extraUnitPrice = getNumber(
      section.dataset.extraChutneyPriceCents,
      350
    );

    const extraPrice = extraQuantity * extraUnitPrice;
    const finalPrice = basePrice + extraPrice;
    const formattedFinalPrice = formatMoney(section, finalPrice);

    const totalPrice = section.querySelector(
      '[data-insieme-total-price]'
    );

    const stickyPrice = section.querySelector(
      '[data-insieme-sticky-price]'
    );

    if (totalPrice) {
      totalPrice.textContent = formattedFinalPrice;
    }

    if (stickyPrice) {
      stickyPrice.textContent = formattedFinalPrice;
    }

    section.dataset.basePrice = String(basePrice);
    section.dataset.extraPrice = String(extraPrice);
    section.dataset.finalPrice = String(finalPrice);
  }

  function updateInterface(section) {
    updateProductQuantityInterface(section);
    updateChutneyCards(section);
    updateChutneyProgress(section);
    updateAccompaniments(section);
    updatePrices(section);
  }

  function selectFormat(section, selectedCard) {
    if (!section || !selectedCard || selectedCard.disabled) return;

    const variantId = selectedCard.dataset.variantId;
    const pricePerPerson = selectedCard.dataset.pricePerPerson;

    section.querySelectorAll(FORMAT_SELECTOR).forEach((card) => {
      const isSelected = card === selectedCard;

      card.classList.toggle('is-selected', isSelected);
      card.setAttribute('aria-pressed', String(isSelected));

      const check = card.querySelector('[data-insieme-check]');

      if (check) {
        check.hidden = !isSelected;
      }
    });

    const personPrice = section.querySelector(
      '[data-insieme-person-price]'
    );

    const variantInput = section.querySelector(
      '[data-insieme-variant-input]'
    );

    if (personPrice && pricePerPerson) {
      personPrice.textContent = pricePerPerson;
    }

    if (variantInput && variantId) {
      variantInput.value = variantId;

      variantInput.dispatchEvent(
        new Event('change', {
          bubbles: true
        })
      );
    }

    if (variantId) {
      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.set('variant', variantId);

      window.history.replaceState(
        {},
        '',
        currentUrl.toString()
      );
    }

    updateInterface(section);

    section.dispatchEvent(
      new CustomEvent('insieme:variant-change', {
        bubbles: true,
        detail: {
          variantId,
          price: selectedCard.dataset.price,
          people: selectedCard.dataset.people,
          includedChutneys:
            selectedCard.dataset.includedChutneys,
          quantity: getProductQuantity(section)
        }
      })
    );
  }

  function changeProductQuantity(section, difference) {
    const currentQuantity = getProductQuantity(section);

    const newQuantity = clamp(
      currentQuantity + difference,
      MIN_PRODUCT_QUANTITY,
      MAX_PRODUCT_QUANTITY
    );

    if (newQuantity === currentQuantity) return;

    section.dataset.quantity = String(newQuantity);

    updateInterface(section);

    section.dispatchEvent(
      new CustomEvent('insieme:quantity-change', {
        bubbles: true,
        detail: {
          quantity: newQuantity,
          requiredChutneys: getRequiredChutneys(section)
        }
      })
    );
  }

  function changeChutneyQuantity(section, card, difference) {
    const currentQuantity = getNumber(
      card.dataset.chutneyQuantity,
      0
    );

    const newQuantity = clamp(
      currentQuantity + difference,
      0,
      MAX_CHUTNEY_QUANTITY
    );

    if (newQuantity === currentQuantity) return;

    card.dataset.chutneyQuantity = String(newQuantity);

    updateInterface(section);

    section.dispatchEvent(
      new CustomEvent('insieme:chutney-change', {
        bubbles: true,
        detail: {
          id: card.dataset.chutneyId,
          name: card.dataset.chutneyName,
          quantity: newQuantity,
          selectedChutneys: getSelectedChutneys(section),
          requiredChutneys: getRequiredChutneys(section),
          extraChutneys: getExtraChutneys(section)
        }
      })
    );
  }

  function changeGlutenFreePortions(section, difference) {
    const root = section.querySelector(ACCOMPANIMENT_SELECTOR);
    if (!root) return;

    const current = getGlutenFreePortions(section);
    const next = clamp(current + difference, 0, getPeople(section));
    if (next === current) return;

    root.dataset.glutenFreePortions = String(next);
    updateAccompaniments(section);

    section.dispatchEvent(
      new CustomEvent('insieme:accompaniment-change', {
        bubbles: true,
        detail: {
          people: getPeople(section),
          standardPortions: Math.max(0, getPeople(section) - next),
          glutenFreePortions: next
        }
      })
    );
  }

  function initialiseSection(section) {
    if (
      !section ||
      section.dataset.insiemeControllerReady === 'true'
    ) {
      return;
    }

    section.dataset.insiemeControllerReady = 'true';

    section.addEventListener('click', (event) => {
      const formatCard = event.target.closest(FORMAT_SELECTOR);

      if (formatCard && section.contains(formatCard)) {
        event.preventDefault();
        selectFormat(section, formatCard);
        return;
      }

      const productDecrease = event.target.closest(
        '[data-insieme-quantity-decrease]'
      );

      if (productDecrease && section.contains(productDecrease)) {
        event.preventDefault();
        changeProductQuantity(section, -1);
        return;
      }

      const productIncrease = event.target.closest(
        '[data-insieme-quantity-increase]'
      );

      if (productIncrease && section.contains(productIncrease)) {
        event.preventDefault();
        changeProductQuantity(section, 1);
        return;
      }

      const chutneyDecrease = event.target.closest(
        '[data-insieme-chutney-decrease]'
      );

      if (chutneyDecrease && section.contains(chutneyDecrease)) {
        event.preventDefault();

        const chutneyCard = chutneyDecrease.closest(
          CHUTNEY_SELECTOR
        );

        if (chutneyCard) {
          changeChutneyQuantity(section, chutneyCard, -1);
        }

        return;
      }

      const chutneyIncrease = event.target.closest(
        '[data-insieme-chutney-increase]'
      );

      if (chutneyIncrease && section.contains(chutneyIncrease)) {
        event.preventDefault();

        const chutneyCard = chutneyIncrease.closest(
          CHUTNEY_SELECTOR
        );

        if (chutneyCard) {
          changeChutneyQuantity(section, chutneyCard, 1);
        }

        return;
      }

      const glutenFreeDecrease = event.target.closest(
        '[data-insieme-gluten-free-decrease]'
      );

      if (glutenFreeDecrease && section.contains(glutenFreeDecrease)) {
        event.preventDefault();
        changeGlutenFreePortions(section, -1);
        return;
      }

      const glutenFreeIncrease = event.target.closest(
        '[data-insieme-gluten-free-increase]'
      );

      if (glutenFreeIncrease && section.contains(glutenFreeIncrease)) {
        event.preventDefault();
        changeGlutenFreePortions(section, 1);
      }
    });

    const initiallySelected = getSelectedFormat(section);

    if (initiallySelected) {
      selectFormat(section, initiallySelected);
    } else {
      updateInterface(section);
    }
  }

  function initialiseAll(root = document) {
    if (root.matches?.(SECTION_SELECTOR)) {
      initialiseSection(root);
    }

    root
      .querySelectorAll?.(SECTION_SELECTOR)
      .forEach(initialiseSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => initialiseAll()
    );
  } else {
    initialiseAll();
  }

  document.addEventListener(
    'shopify:section:load',
    (event) => initialiseAll(event.target)
  );
})();
